/**
 * @voltxampere/thermal — R_thermal canonical table (Sprint 34-1)
 *
 * Per-component-type thermal resistance + max rated temperature
 * + assumed ambient. Datasheet typical değerler — JEDEC/IPC
 * standart koşullar (still air, no heat sink unless variant
 * explicitly specifies).
 *
 * Higher R_th → component heats up MORE per Watt dissipated.
 * Examples:
 *   - axial 1/4 W resistor: 80 °C/W (small body, mostly leads)
 *   - SMD 0805 resistor: 100 °C/W (less metal mass than axial)
 *   - TO-92 BJT: 200 °C/W (plastic package, no metal tab)
 *   - TO-220 transistor: 5 °C/W (large metal tab dissipates well)
 *   - TO-220 with NO heat sink quoted here — sink lowers further
 *
 * Source: ADR-0056 (Sprint 34 thermal model methodology). Manufacturer
 * datasheets cross-checked: Vishay (resistors), ON Semi (BJT/MOSFET),
 * Cree (LEDs), Nichicon (electrolytics).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
export const R_THERMAL_TABLE = [
    // Resistors — through-hole + SMD
    { type: "resistor", variant: "axial-1/4W", R_th_CperW: 80, T_max_C: 150, T_ambient_C: 25 },
    { type: "resistor", variant: "axial-1W", R_th_CperW: 60, T_max_C: 150, T_ambient_C: 25 },
    { type: "resistor", variant: "smd-0805", R_th_CperW: 100, T_max_C: 125, T_ambient_C: 25 },
    // Diodes — small-signal vs power
    { type: "diode", variant: "1n4148", R_th_CperW: 300, T_max_C: 150, T_ambient_C: 25 },
    { type: "diode", variant: "1n4007", R_th_CperW: 100, T_max_C: 150, T_ambient_C: 25 },
    // LEDs — through-hole vs SMD
    { type: "led", variant: "5mm-thru", R_th_CperW: 400, T_max_C: 85, T_ambient_C: 25 },
    { type: "led", variant: "smd-0805", R_th_CperW: 500, T_max_C: 85, T_ambient_C: 25 },
    // BJTs — TO-92 small-signal + TO-220 power
    { type: "bjt-npn", variant: "to92", R_th_CperW: 200, T_max_C: 150, T_ambient_C: 25 },
    { type: "bjt-pnp", variant: "to92", R_th_CperW: 200, T_max_C: 150, T_ambient_C: 25 },
    { type: "bjt-npn", variant: "to220", R_th_CperW: 5, T_max_C: 150, T_ambient_C: 25 },
    // MOSFETs — TO-220 power
    { type: "mosfet-nmos", variant: "to220", R_th_CperW: 2.5, T_max_C: 175, T_ambient_C: 25 },
    { type: "mosfet-pmos", variant: "to220", R_th_CperW: 2.5, T_max_C: 175, T_ambient_C: 25 },
    // Capacitors — ESR-based simplified (ESR varies with type)
    { type: "capacitor", variant: "electrolytic", R_th_CperW: 50, T_max_C: 85, T_ambient_C: 25 },
    { type: "capacitor", variant: "ceramic", R_th_CperW: 150, T_max_C: 125, T_ambient_C: 25 },
    // Inductors — air-core, magnetic loss + I²R copper
    { type: "inductor", variant: "air-core", R_th_CperW: 200, T_max_C: 125, T_ambient_C: 25 },
    // Op-amps — DIP-8
    { type: "opamp", variant: "dip-8", R_th_CperW: 120, T_max_C: 150, T_ambient_C: 25 },
    // ── Sprint 38 Aşama 3 başlangıç — 6 yeni entry (16 → 22) ─────────
    // Power MOSFETs — Sprint 38-2 datasheet variants (TO-220 + SOT-23).
    { type: "mosfet-nmos", variant: "irf540-to220", R_th_CperW: 1.5, T_max_C: 175, T_ambient_C: 25 },
    { type: "mosfet-pmos", variant: "irf9540-to220", R_th_CperW: 1.5, T_max_C: 175, T_ambient_C: 25 },
    { type: "mosfet-nmos", variant: "bss138-sot23", R_th_CperW: 600, T_max_C: 150, T_ambient_C: 25 },
    // Schottky diodes — Sprint 38-4 (DO-41 axial).
    { type: "diode", variant: "schottky-do41", R_th_CperW: 80, T_max_C: 125, T_ambient_C: 25 },
    // Power resistors — Sprint 38-4 (wirewound + aluminum-clad chassis).
    { type: "resistor", variant: "5w-wirewound", R_th_CperW: 14, T_max_C: 250, T_ambient_C: 25 },
    { type: "resistor", variant: "10w-alclad", R_th_CperW: 7, T_max_C: 250, T_ambient_C: 25 },
    // ── Sprint 39-5 — Op-amp package coverage (22 → 28) ──────────────
    // Six new op-amp paket variants accompany the Sprint 39 op-amp
    // family expansion (LM358/LM324/TL072/TL082/NE5532/OPA2134/LM393/
    // LM339/INA126). DIP packages dissipate better than surface-mount
    // because of their longer leads + larger plastic body; SOIC and
    // TSSOP penalise heat shed proportionally with shrinkage. Numbers
    // taken from the corresponding TI / Burr-Brown datasheet thermal
    // tables and matched against the existing op-amp DIP-8 baseline
    // (120 °C/W, kept for legacy generic placements).
    { type: "opamp", variant: "lm358-dip-8", R_th_CperW: 100, T_max_C: 150, T_ambient_C: 25 },
    { type: "opamp", variant: "lm358-soic-8", R_th_CperW: 145, T_max_C: 150, T_ambient_C: 25 },
    { type: "opamp", variant: "lm324-dip-14", R_th_CperW: 80, T_max_C: 150, T_ambient_C: 25 },
    { type: "opamp", variant: "lm324-soic-14", R_th_CperW: 110, T_max_C: 150, T_ambient_C: 25 },
    { type: "opamp", variant: "tl072-tssop-8", R_th_CperW: 200, T_max_C: 150, T_ambient_C: 25 },
    { type: "opamp", variant: "tl082-msop-8", R_th_CperW: 220, T_max_C: 150, T_ambient_C: 25 },
    // ── Sprint 40-2 — Linear regulator paket coverage (28 → 32) ──────
    // Linear regulators carry the same canonical engine type as the
    // base voltage-source (Sprint 4 vdc stamp), but their package
    // thermal characteristics differ wildly between TO-220 (large
    // tabbed plastic, designed to bolt onto a heat sink) and SOT-223
    // (surface-mount, dissipates through the PCB copper). Thermal
    // numbers come from the Fairchild MC78xx + TI LM317 + AMS1117
    // datasheets; "to-220-fixed-regulator" / "to-220-adjustable-
    // regulator" / "sot-223-ams1117" / "to-92-low-power" cover the
    // packages users will encounter from the JLCPCB / LCSC catalog.
    {
        type: "voltage-source",
        variant: "to-220-fixed-regulator",
        R_th_CperW: 50,
        T_max_C: 150,
        T_ambient_C: 25,
    },
    {
        type: "voltage-source",
        variant: "to-220-adjustable-regulator",
        R_th_CperW: 50,
        T_max_C: 150,
        T_ambient_C: 25,
    },
    {
        type: "voltage-source",
        variant: "sot-223-ams1117",
        R_th_CperW: 70,
        T_max_C: 150,
        T_ambient_C: 25,
    },
    {
        type: "voltage-source",
        variant: "to-92-low-power",
        R_th_CperW: 200,
        T_max_C: 150,
        T_ambient_C: 25,
    },
];
/**
 * Conservative fallback for components not present in the table.
 * R_th=200 °C/W is a mid-range value; T_max=125 °C is industry
 * baseline (commercial-grade plastic package). Better than throwing
 * — Sprint 34 prioritizes coverage over per-type accuracy for
 * less-common parts. Aşama 3 S38+ extends the table.
 */
export const R_THERMAL_FALLBACK = {
    type: "unknown",
    R_th_CperW: 200,
    T_max_C: 125,
    T_ambient_C: 25,
};
/**
 * Resolve R_thermal entry for a component. Lookup order:
 *   1. Exact (type + variant) match.
 *   2. Type-only match (any variant of the same type).
 *   3. Conservative fallback ({@link R_THERMAL_FALLBACK}).
 */
export function getRThermalEntry(type, variant) {
    if (variant !== undefined) {
        const exact = R_THERMAL_TABLE.find((e) => e.type === type && e.variant === variant);
        if (exact !== undefined)
            return exact;
    }
    const typeMatch = R_THERMAL_TABLE.find((e) => e.type === type);
    if (typeMatch !== undefined)
        return typeMatch;
    return R_THERMAL_FALLBACK;
}
//# sourceMappingURL=r-thermal-table.js.map